/**
 * V2 — Cycle de vie d'un bien dérivé de la donnée (zéro nouveau status enum).
 *
 * En V1, le statut "EN PRÉPARATION" servait de garde-fou : tant qu'il était
 * actif, le bien ne devait pas remonter chez le collaborateur. En V2, ce
 * statut a été retiré de la fiche — la distinction "prêt à publier / pas
 * prêt" est désormais portée par la COMPLÉTUDE des champs (CLevel requis +
 * mailing), pas par un statut DB.
 *
 * - **isCLevelReady(bien)** : true si tous les champs CLEVEL_REQUIRED sont
 *   remplis. C'est le seuil de visibilité pour collab + publication +
 *   prestataire. Tant que c'est false, le bien reste dans le périmètre
 *   CLevel/admin uniquement.
 *
 * - **getLifecycleStage(bien)** : stage métier dérivé (DRAFT_CLEVEL →
 *   READY_FOR_ELS → ELS_PLANNED → … → LIVE → ARCHIVED).
 *
 * Pas de migration DB, pas de nouveau champ — tout est calculé.
 */

import type { BienRow } from "@/schemas/bien";
import {
  getBaseRequiredFields,
  getCycleRequiredFields,
  hasExitCycleInFlight,
  isCycleOuvert,
  getCycleTodoFields,
  isElsLance,
  isPebDone,
  isPebLance,
} from "@/lib/bienPrepValidation";
import { isMissionEligible, isForRent } from "@/lib/transaction";
import { checkElsStates } from "@/v2/components/CheckElsBadges";

// Re-export pour les consommateurs V2 (anciennes imports).
export { ACTIVE_REQUIRED_FIELDS as CLEVEL_REQUIRED_FIELDS } from "@/lib/bienPrepValidation";
// `hasExitCycleInFlight` a déménagé dans bienPrepValidation (2026-09-15,
// `isCycleOuvert` en dépend) — ré-exporté ici pour les imports existants.
export { hasExitCycleInFlight } from "@/lib/bienPrepValidation";

/**
 * AXE 1 — Pipeline ELS & PEB. Stages opérationnels purs du cycle de sortie
 * (renon → ELS et/ou PEB → finalisation SRS), sans aucune notion de
 * publication portails. Voir aussi PublicationStage (axe 2) pour la
 * visibilité marketing, qui est orthogonale — depuis le 2026-09-15 elle ne
 * dépend plus d'aucun champ du cycle (plan « deux cycles »).
 */
export type ElsPipelineStage =
  | "draft_clevel"       // identité incomplète (ref/projet/type/adresse/ville/étage/libre le) — reste chez le C-Level
  | "sans_cycle"         // identité OK, aucun cycle de sortie ouvert — se publie, se maile, hors kanban
  | "draft_cycle"        // cycle ouvert mais collaborateur manquant (ELS ou PEB lancé) — bloquant, hors kanban
  | "sans_process"       // cycle ouvert (dates) mais ni ELS ni PEB lancés — hors kanban
  | "ready_for_els"      // au moins un process lancé à assigner, ou lancé en attente de RDV (col. 1 « À planifier »)
  | "a_lancer"           // tous les process à faire ont leur prestataire/expert, au moins une mission pas encore lancée (col. 1 « À lancer »)
  | "els_planned"        // tous les process lancés ont leur date (col. 2 « Planifié »)
  | "els_done"           // tous les process lancés faits, ELS lancé → SRS à finaliser (col. 3)
  | "cycle_done"         // tous les process lancés faits, SANS ELS → rien à clôturer (hors kanban)
  | "closing"            // dossierCloture=true, attente validation finale C-Level
  | "closed"             // dossierClos=true, dossier SRS fermé par C-Level (terminal)
  | "loue"               // bien loué (état stable, terminal)
  | "vendu"              // bien vendu (état stable, terminal)
  | "archived";          // legacy / hors workflow

/** Alias rétrocompat. Anciennement appelée LifecycleStage. */
export type LifecycleStage = ElsPipelineStage;

export interface LifecycleResult {
  stage: ElsPipelineStage;
  /** True si le bien est visible chez collab / publication / prestataire. */
  isCLevelReady: boolean;
  /** Liste des champs requis manquants (vide si isCLevelReady). */
  missingFields: string[];
  /** Clé i18n du label humain pour l'UI (à passer à t()). */
  labelKey: string;
  /** Couleur sémantique pour l'UI ("danger" = bloqué, "warning" = action requise…). */
  color: StageColor;
}

type StageColor = "danger" | "warning" | "accent" | "success" | "muted";

// Libellés alignés sur les colonnes du Pipeline kanban et les tuiles
// Dashboard, pour qu'un même bien dise la même chose partout.
// labelKey = clé i18n (namespace common). loue/vendu réutilisent les clés de
// statut de disponibilité existantes (options.disponibilite.*).
const STAGE_META: Record<ElsPipelineStage, { labelKey: string; color: StageColor }> = {
  draft_clevel:    { labelKey: "v2.lifecycle.draft_clevel",   color: "danger"  },
  sans_cycle:      { labelKey: "v2.lifecycle.sans_cycle",     color: "muted"   },
  draft_cycle:     { labelKey: "v2.lifecycle.draft_cycle",    color: "warning" },
  sans_process:    { labelKey: "v2.lifecycle.sans_process",   color: "muted"   },
  ready_for_els:   { labelKey: "v2.lifecycle.ready_for_els",  color: "warning" },
  a_lancer:        { labelKey: "v2.lifecycle.a_lancer",       color: "warning" },
  els_planned:     { labelKey: "v2.lifecycle.els_planned",    color: "accent"  },
  els_done:        { labelKey: "v2.lifecycle.els_done",       color: "warning" },
  cycle_done:      { labelKey: "v2.lifecycle.cycle_done",     color: "muted"   },
  closing:         { labelKey: "v2.lifecycle.closing",        color: "warning" },
  closed:          { labelKey: "v2.lifecycle.closed",         color: "success" },
  loue:            { labelKey: "options.disponibilite.loue",  color: "success" },
  vendu:           { labelKey: "options.disponibilite.vendu", color: "muted"   },
  archived:        { labelKey: "v2.lifecycle.archived",       color: "muted"   },
};

// ---------------------------------------------------------------------------
// AXE 2 — Publication. Visibilité du bien sur les portails publics
// (Immoweb, site web). Indépendant de l'axe ELS.
// ---------------------------------------------------------------------------

export type PublicationStage =
  | "retire"        // LOUÉ ou VENDU — bien hors marché
  | "publie_stale"  // Publié mais modifié depuis (updated_at > last_published_at)
  | "publie"        // Publié et à jour sur les portails
  | "brouillon";    // statutPublication != "Publié" (et bien actif)

export interface PublicationResult {
  stage: PublicationStage;
  /** Clé i18n du label humain pour l'UI (à passer à t()). */
  labelKey: string;
  color: StageColor;
}

// labelKey = clé i18n (namespace common). retire/publie/brouillon réutilisent
// les clés de statut de publication existantes (biens.statutPublicationOptions.*).
const PUBLICATION_META: Record<PublicationStage, { labelKey: string; color: StageColor }> = {
  retire:       { labelKey: "biens.statutPublicationOptions.retire",    color: "muted"   },
  publie_stale: { labelKey: "v2.publication.publie_stale",              color: "warning" },
  publie:       { labelKey: "biens.statutPublicationOptions.publie",    color: "success" },
  brouillon:    { labelKey: "biens.statutPublicationOptions.brouillon", color: "muted"   },
};

/**
 * Calcule l'état de publication d'un bien. Orthogonal au stage ELS.
 *
 * Priorités :
 *   1. LOUÉ / VENDU → "retire" (le bien est hors marché peu importe son
 *      ancien état de publication).
 *   2. statutPublication = "Publié" :
 *      - si updated_at > last_published_at → "publie_stale" (modifs non
 *        repoussées sur les portails depuis la dernière publication).
 *      - sinon → "publie" (à jour).
 *   3. Sinon → "brouillon" (jamais publié ou explicitement dépublié).
 */
export function getPublicationStage(bien: BienRow): PublicationResult {
  const dispo = (bien.disponibilite || "").toUpperCase();
  if (dispo === "LOUÉ" || dispo === "VENDU") {
    return resultPub("retire");
  }
  if (bien.statutPublication === "Publié") {
    // Staleness sur le CONTENU PORTAIL uniquement (content_updated_at, trigger
    // migration 066), pas sur updated_at qui bouge à chaque sauvegarde de champ
    // (SRS/ELS/locataires/publication/PEB opérationnel/internes). Fallback
    // updated_at si la colonne n'est pas encore là. Même règle que le helper V1
    // hasUnpublishedChanges (src/lib/publicationHelpers.ts).
    const contentTs = bien.content_updated_at ?? bien.updated_at;
    const upd = contentTs ? new Date(contentTs).getTime() : 0;
    const pub = bien.last_published_at ? new Date(bien.last_published_at).getTime() : 0;
    if (pub > 0 && upd > pub) return resultPub("publie_stale");
    return resultPub("publie");
  }
  return resultPub("brouillon");
}

function resultPub(stage: PublicationStage): PublicationResult {
  return { stage, ...PUBLICATION_META[stage] };
}

/**
 * Ordre de priorité pour le tri (le plus actionnable en haut) :
 *   publie_stale (1) → brouillon (2) → publie (3) → retire (4)
 */
export function publicationSortValue(stage: PublicationStage): number {
  return { publie_stale: 1, brouillon: 2, publie: 3, retire: 4 }[stage];
}

/**
 * Ordre du cycle pour le tri de la colonne « Pipeline ELS » du catalogue :
 * identité → sans cycle → cycle à compléter → sans process → à planifier →
 * planifié → à clôturer → process terminé → SRS à clôturer → clos → loué →
 * vendu → archivé. Avant (signalé Thibaud 2026-09-15 en testant HK_H4.4), le
 * tri se faisait sur la CLÉ interne, alphabétiquement : « À planifier »
 * (`ready_for_els`) tombait entre « Loué » et « Aucun cycle », loin de
 * « Cycle à compléter » (`draft_cycle`) — un ordre sans rapport avec les
 * libellés affichés. Même principe que `publicationSortValue`.
 */
const LIFECYCLE_SORT_ORDER: Record<LifecycleStage, number> = {
  draft_clevel: 1,
  sans_cycle: 2,
  draft_cycle: 3,
  sans_process: 4,
  ready_for_els: 5,
  a_lancer: 6,
  els_planned: 7,
  els_done: 8,
  cycle_done: 9,
  closing: 10,
  closed: 11,
  loue: 12,
  vendu: 13,
  archived: 14,
};

export function lifecycleSortValue(stage: LifecycleStage): number {
  return LIFECYCLE_SORT_ORDER[stage];
}

// ---------------------------------------------------------------------------
// Validation par champs (plus de hack EN PRÉPARATION)
// ---------------------------------------------------------------------------

function isFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return !Number.isNaN(value);
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "boolean") return true;
  return Boolean(value);
}

/**
 * Calcule si les champs CLevel requis sont tous remplis.
 *
 * Convention : les biens LOUÉ et VENDU sont considérés OK (état stable —
 * ils ont eu leur vie utile, pas besoin d'auditer leur complétude).
 *
 * Exporté car réutilisé par dashboardBuckets.ts et tasks.ts.
 */
export function isCLevelReady(bien: BienRow): { ok: boolean; missing: string[] } {
  const dispo = (bien.disponibilite || "").toUpperCase();
  if (dispo === "LOUÉ" || dispo === "VENDU") {
    return { ok: true, missing: [] };
  }
  // Champs requis contextualisés : un bien vide (entrée en gestion sans
  // locataire sortant) n'exige pas renonRecuLe.
  const missing = getBaseRequiredFields(bien as Record<string, unknown>).filter(
    (k) => !isFilled((bien as Record<string, unknown>)[k]),
  );
  return { ok: missing.length === 0, missing };
}

export function getLifecycleStage(bien: BienRow): LifecycleResult {
  const { ok, missing } = isCLevelReady(bien);
  const dispo = (bien.disponibilite || "").toUpperCase();

  // Stages stables d'abord
  if (dispo === "VENDU") return resultOf("vendu", true, []);
  if (dispo === "LOUÉ") {
    // Relocation anticipée : le bien est re-LOUÉ (au locataire B) alors que la
    // sortie du précédent (A) reste à dérouler côté ELS/SRS. Tant que ce cycle
    // de sortie est "en vol" (hasExitCycleInFlight : date ELS/PEB posée,
    // prestataire assigné, ou dossier en cours de clôture) et pas encore
    // clôturé (dossierClos), le bien doit continuer à apparaître dans le
    // Pipeline ELS à SA vraie étape opérationnelle — sinon un ELS lancé
    // disparaît purement et simplement du pipeline dès que le nouveau
    // locataire est capturé (signalé Thibaud 2026-08-20 : « un ELS lancé
    // doit rester actif, c'est un process différent »). Seul un cycle
    // réellement clos (dossierClos) ou totalement inexistant retombe sur
    // l'état stable "loue".
    //
    // « Inexistant » = `cycleOuvertLe` null (migration 135) : le passage
    // DISPONIBLE → LOUÉ ne le vide pas, donc une sortie en vol a toujours son
    // cycle ouvert. Des marqueurs orphelins sans cycle (données héritées,
    // requalification VENDU → LOUÉ de la Phase 3) ne sont PAS une sortie en
    // vol — sans ce garde, `operationalElsStage` répondrait « sans_process »
    // pour un bien loué. Même prédicat que `isPrestataireMissionOpen`.
    if (!bien.dossierClos && hasExitCycleInFlight(bien) && isCycleOuvert(bien as Record<string, unknown>)) {
      return operationalElsStage(bien);
    }
    return resultOf("loue", true, []);
  }

  // Dossier SRS fermé par le C-Level (étape 2, dossierClos=true) = état
  // TERMINAL : le bien sort du pipeline ELS (il n'y reviendra qu'au prochain
  // cycle, après reset au passage en loué — cf. resetElsCycleForLoue). Priorité
  // sur tout le reste, comme loué/vendu : sans ça il retombait en "closing"
  // (dossierCloture reste true) et restait affiché dans "SRS admin à clôturer"
  // même après fermeture (bug 2026-06-03).
  if (bien.dossierClos) return resultOf("closed", true, []);

  if (!ok) return resultOf("draft_clevel", false, missing);

  // Aucun cycle de sortie ouvert (ni renon, ni marqueur ELS/PEB) : le bien
  // n'a rien à faire dans le pipeline — il se publie et se maile, c'est tout
  // (plan « deux cycles », 2026-09-15). Remplace l'ancien `bien_vide`, qui
  // déduisait ça de l'origine du bien (`entreeGestion`) au lieu du fait.
  if (!isCycleOuvert(bien as Record<string, unknown>)) {
    return resultOf("sans_cycle", true, []);
  }

  // Cycle ouvert mais incomplet : collaborateur (si ELS lancé) ou expert (si
  // PEB lancé) manquant. Reste chez le C-Level — ne bloque PAS la publication
  // (l'identité est OK), mais le collab/prestataire ne le voit pas encore.
  const cycle = isCycleReady(bien);
  if (!cycle.ok) return resultOf("draft_cycle", true, cycle.missing);

  // Cycle complet : on dérive l'étape opérationnelle des process lancés.
  return operationalElsStage(bien);
}

/**
 * Étape opérationnelle du cycle ELS/PEB en cours (colonnes 1 à 4 du Pipeline),
 * indépendamment de `disponibilite` — appelée aussi bien pour un bien
 * DISPONIBLE que pour un bien LOUÉ dont le cycle de sortie précédent est
 * encore "en vol" (relocation anticipée, cf. hasExitCycleInFlight).
 *
 * Les flags de clôture PRENNENT PRIORITÉ sur les autres champs (un bien dont
 * le dossier SRS est finalisé EST plus avancé dans le pipeline, même si
 * edlDatePrevue a été vidé entre-temps — data drift typique).
 *
 * NB : dossierClos=true (CEO a fermé le dossier) ne donne pas un stage ELS
 * spécifique — géré par l'appelant AVANT d'arriver ici (retombe sur "closed"
 * ou "loue" selon le contexte).
 */
function operationalElsStage(bien: BienRow): LifecycleResult {
  if (bien.dossierCloture) {
    return resultOf("closing", true, []);
  }

  // Historique : le PEB est intégré au flux depuis la spec user 2026-06-03
  // (une étape n'est franchie que si l'ELS ET le PEB le sont) ; `elsRequis`
  // (2026-09-14) puis `pebRequis` (2026-09-15) n'étaient lus nulle part ici,
  // d'où des biens bloqués sur "Prêt pour ELS" après avoir décoché une case.
  // ELS et PEB : deux process AU MÊME NIVEAU (décision Thibaud 2026-09-15,
  // « il peut y avoir l'un et pas l'autre »). Chacun est lancé ou non, chacun
  // a son état ; la colonne du bien = le MOINS avancé des process lancés.
  const draft = bien as Record<string, unknown>;
  const elsLance = isElsLance(draft);
  const pebLance = isPebLance(draft);

  // Cycle ouvert (dates seules) mais aucun process lancé : rien à suivre dans
  // le kanban, rien à clôturer.
  if (!elsLance && !pebLance) {
    return resultOf("sans_process", true, []);
  }

  const s = checkElsStates(bien);
  // ELS fait = expertise validée (`statutEle`). PEB fait = expertise de CE
  // cycle terminée (`statutPeb = expertise_ok`, cf. `isPebDone`) — plus la
  // validité du certificat en vigueur, qui faisait sortir en « Process
  // terminé » un PEB demandé explicitement sur un certificat encore valide
  // (HK_H4.4, 2026-09-15). Un process non lancé compte comme fait : il ne
  // retient rien.
  const elsDone = !elsLance || s.els === "success";
  const pebDone = !pebLance || isPebDone(draft);

  // Col 3 "À clôturer (collab)" : tout ce qui est lancé est fait. Avec un ELS
  // il reste la finalisation SRS ; un PEB seul se termine ici, sans clôture
  // financière — il sort du kanban.
  if (elsDone && pebDone) {
    return resultOf(elsLance ? "els_done" : "cycle_done", true, []);
  }

  // Rang de chaque process encore à faire — la colonne = le MOINS avancé :
  //   1 à assigner : personne de désigné (ou refus en attente de réassignation)
  //   2 à lancer   : prestataire/expert désigné, mission pas encore lancée —
  //                  c'est au collaborateur / C-Level d'agir (ajouté
  //                  2026-09-15 soir, demande Thibaud : « comment je sais que
  //                  HK est bloqué ? » — « À planifier » mélangeait ce cas
  //                  avec l'attente du rendez-vous, qui est au prestataire)
  //   3 lancé, en attente du rendez-vous — ou ELS « amiable » / « autre »,
  //                  sans mission à lancer
  //   4 daté
  //   5 fait (ou non lancé)
  const special = (id: unknown) => id === "amiable" || id === "autre";
  const real = (id: unknown) => typeof id === "string" && id.trim().length > 0 && !special(id);
  const rankOf = (done: boolean, date: unknown, launchedAt: unknown, assignee: unknown): number =>
    done ? 5 : date ? 4 : launchedAt || special(assignee) ? 3 : real(assignee) ? 2 : 1;
  const rank = Math.min(
    rankOf(elsDone, bien.edlDatePrevue, bien.elsMissionLanceeAt, bien.prestataireEle),
    rankOf(pebDone, bien.pebDatePrevue, bien.pebMissionLanceeAt, bien.expertPeb),
  );

  // Col 2 "Planifié" : chaque process encore à faire a sa date.
  if (rank >= 4) return resultOf("els_planned", true, []);
  // Col 1 "À lancer" : tout le monde est désigné, il reste à lancer une mission.
  if (rank === 2) return resultOf("a_lancer", true, []);
  // Col 1 "À planifier" : quelqu'un à assigner, ou mission lancée sans date.
  return resultOf("ready_for_els", true, []);
}

/**
 * « Cycle à compléter » (chip catalogue, tuile dashboard, tâche C-Level) :
 * cycle ouvert où il manque quelqu'un — le collaborateur (bloquant, stage
 * `draft_cycle`, hors pipeline) OU le prestataire ELS / l'expert PEB d'un
 * process lancé (non bloquants : le bien est déjà dans le pipeline, décision
 * Thibaud 2026-09-15 soir).
 */
export function isCycleIncomplet(bien: BienRow): boolean {
  const draft = bien as Record<string, unknown>;
  if (!isCycleOuvert(draft)) return false;
  if (!isCycleReady(bien).ok) return true;
  return getCycleTodoFields(draft).some((k) => !isFilled(draft[k]));
}

function resultOf(stage: LifecycleStage, ok: boolean, missing: string[]): LifecycleResult {
  return {
    stage,
    isCLevelReady: ok,
    missingFields: missing,
    labelKey: STAGE_META[stage].labelKey,
    color: STAGE_META[stage].color,
  };
}

// ---------------------------------------------------------------------------
// Helpers de visibilité par rôle (V2 — la garde-fou métier)
// ---------------------------------------------------------------------------

import type { AppRole } from "@/contexts/RoleContext";

/**
 * Décide si un bien est visible dans les vues OPÉRATIONNELLES d'un rôle :
 * Inbox, Pipeline, Apartments for Rent, Missions prestataire.
 *
 * - admin / c_level : voient tout (catalogue + tableaux de bord).
 * - autres rôles : ne voient un bien que s'il est `isCLevelReady`. C'est
 *   la garantie qu'aucun bien à moitié rempli ne déclenche du travail
 *   prématuré (planifier une ELS sans collab assigné, etc.).
 */
export function isVisibleForRole(bien: BienRow, role: AppRole): boolean {
  if (role === "admin" || role === "c_level") return true;
  return isCLevelReady(bien).ok;
}

/**
 * Filtre une liste de biens pour ne garder que ceux visibles au rôle.
 * Retourne aussi le compte des biens bloqués (pour afficher un bandeau).
 */
export function filterVisibleBiens(biens: BienRow[], role: AppRole): {
  visible: BienRow[];
  blocked: number;
} {
  if (role === "admin" || role === "c_level") {
    return { visible: biens, blocked: 0 };
  }
  let blocked = 0;
  const visible: BienRow[] = [];
  for (const b of biens) {
    if (isCLevelReady(b).ok) visible.push(b);
    else blocked++;
  }
  return { visible, blocked };
}

// ---------------------------------------------------------------------------
// Helpers Apartments for Rent (vue marketing-ready)
// ---------------------------------------------------------------------------

// Import dynamique pour éviter une dépendance circulaire avec dashboardBuckets.
// (dashboardBuckets importe isCLevelReady → on garde le helper léger ici.)
import { ACTIVE_MAILING_FIELDS, getConditionalMailingFields, type BienDraft } from "@/lib/bienPrepValidation";

function checkMailingComplete(bien: BienRow): boolean {
  const draft = bien as BienDraft;
  const fields = [...ACTIVE_MAILING_FIELDS, ...getConditionalMailingFields(draft)];
  return fields.every((k) => isFilled((draft as Record<string, unknown>)[k]));
}

/**
 * Critère "à louer" pour le workspace Apartments for Rent — aligné V1 :
 * - CLevel OK (10 champs requis remplis)
 * - mailing complet (15 champs + conditionnels remplis)
 * - actif (ni LOUÉ ni VENDU)
 *
 * = exactement ce que la V1 `AppartsForRentPage` montre (cf. ligne 535-536
 * : `requiredMissing.length === 0 && mailingMissing.length === 0` ET
 * `disponibilite === EN PRÉPARATION || DISPONIBLE`).
 *
 * ⚠️ « Sous option » (pause de vitrine, cf. lib/sousOption.ts) N'EST PLUS
 * exclu ICI depuis le 2026-08-19 : un bien sous option reste éligible à
 * l'écran, juste classé dans son propre chip plutôt que retiré purement et
 * simplement (même miroir que « Vendu » sur le Catalogue Location — exclu du
 * chip « Tous », visible via son chip dédié). Voir `AppartsForRentPage`.
 */
export function isAppartForRent(bien: BienRow): boolean {
  if (!isCLevelReady(bien).ok) return false;
  const dispo = (bien.disponibilite || "").toUpperCase();
  if (dispo === "LOUÉ" || dispo === "VENDU") return false;
  if (!checkMailingComplete(bien)) return false;
  return true;
}

/**
 * Champs du cycle de sortie manquants (collaborateur si ELS lancé, expert si
 * PEB lancé) — vide si aucun cycle n'est ouvert. Second garde-fou, distinct
 * de l'identité (`isCLevelReady`) : il ne bloque JAMAIS la publication ni le
 * mailing, seulement le pipeline et les missions.
 */
export function isCycleReady(bien: BienRow): { ok: boolean; missing: string[] } {
  const draft = bien as Record<string, unknown>;
  const missing = getCycleRequiredFields(draft).filter((k) => !isFilled(draft[k]));
  return { ok: missing.length === 0, missing };
}

/**
 * Un bien est "ouvert" aux missions prestataires (PEB/ELS) — autrement dit le
 * prestataire assigné en est « prévenu » (la demande apparaît dans son
 * interface) — UNIQUEMENT si (règle métier user 2026-06-03, amendée 2026-07-10) :
 *  - transaction location (`isForRent`), VENDU exclu,
 *  - bien **DISPONIBLE** (pas EN PRÉPARATION : la demande ne part qu'à la mise sur le marché),
 *    OU bien **LOUÉ** avec un cycle de sortie encore en vol (`hasExitCycleInFlight`) —
 *    relocation anticipée : le nouveau bail signé ne doit PAS faire disparaître
 *    l'ELS/PEB de sortie du locataire précédent de la worklist du prestataire,
 *  - **identité remplie** (`isCLevelReady`) ET **cycle complet** (`isCycleReady` :
 *    collaborateur / expert désignés pour les process lancés),
 *  - dossier **non clôturé** (`!dossierClos` : un dossier fermé ne doit plus solliciter le prestataire).
 *
 * Ce prédicat NE filtre PAS l'assignation (expertPeb / prestataireEls) ni le
 * statut de la mission ni le refus — ça reste à la charge de chaque écran
 * (PEB vs ELS, prestataire connecté, etc.). Il borne juste l'éligibilité du bien.
 */
export function isPrestataireMissionOpen(bien: BienRow): boolean {
  if (bien.dossierClos) return false;
  if (!isCLevelReady(bien).ok) return false;
  // Pas de cycle de sortie ouvert → aucune mission à prévenir (plan « deux
  // cycles », 2026-09-15 : un bien en marché sans cycle n'atteint jamais un
  // prestataire).
  if (!isCycleOuvert(bien as Record<string, unknown>)) return false;
  if (!isCycleReady(bien).ok) return false;
  const dispo = (bien.disponibilite || "").toUpperCase();
  if (dispo === "DISPONIBLE") return isMissionEligible(bien);
  // Relocation anticipée : LOUÉ mais sortie du précédent non terminée.
  if (dispo === "LOUÉ") return isForRent(bien.transaction) && hasExitCycleInFlight(bien);
  return false;
}
